/**
 * 조건부 레이아웃 영역 컴포넌트들
 * Single Responsibility: 각 UI 영역의 조건부 렌더링만 담당
 */ import React from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
// 🎨 Styled Components with Theme System
const StyledHeader = styled.header`
  height: 64px;
  background-color: ${({ theme })=>theme.paper};
  border-bottom: 1px solid ${({ theme })=>theme.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
  flex-shrink: 0;
`;
const StyledMainContent = styled.div`
  flex: 1;
  background-color: ${({ theme })=>theme.background};
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;
// 🎯 조건부 사이드바 컴포넌트
export const ConditionalSidebar = ({ show, children, render, className = '', style = {} })=>{
    if (!show) return null;
    return /*#__PURE__*/ React.createElement("aside", {
        className: `main-layout__sidebar ${className}`,
        style: style
    }, render ? render() : children);
};
export const ConditionalSidebarWithAccount = ({ show, children, render, className = '', style = {}, isTabletView = false })=>{
    if (!show) return null;
    // render 함수 실행 및 결과 파싱
    const renderResult = render ? render() : null;
    let mainContent = null;
    let accountSection = null;
    // render 함수가 객체를 반환하는 경우 (메인과 계정 분리)
    if (renderResult && typeof renderResult === 'object' && 'main' in renderResult) {
        mainContent = renderResult.main;
        accountSection = renderResult.account;
    } else {
        // 단순 JSX를 반환하는 경우
        mainContent = renderResult || children;
    }
    // 컨테이너 스타일
    const containerStyle = {
        ...style,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        height: '100%',
        overflow: 'visible'
    };
    // 메인 콘텐츠 스타일
    const contentStyle = {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        paddingBottom: accountSection ? isTabletView ? '80px' : '100px' : '0'
    };
    // AccountSection 스타일
    const accountSectionStyle = {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        padding: isTabletView ? '0' : '0',
        backgroundColor: 'var(--theme-paper, #ffffff)',
        zIndex: 20
    };
    return /*#__PURE__*/ React.createElement("aside", {
        className: `main-layout__sidebar ${className}`,
        style: containerStyle
    }, /*#__PURE__*/ React.createElement("div", {
        style: contentStyle
    }, mainContent), accountSection && /*#__PURE__*/ React.createElement("div", {
        style: accountSectionStyle
    }, accountSection));
};
// 🎯 조건부 오른쪽 사이드바 컴포넌트
export const ConditionalRightSidebar = ({ show, children, render, className = '', style = {}, isResizing = false, onMouseDown, resizeHandleProps })=>{
    if (!show) return null;
    return /*#__PURE__*/ React.createElement("aside", {
        className: `main-layout__right-sidebar ${isResizing ? 'resizing' : ''} ${className}`,
        style: style
    }, resizeHandleProps && /*#__PURE__*/ React.createElement("div", {
        style: {
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '6px',
            cursor: 'col-resize',
            backgroundColor: 'transparent',
            zIndex: 10
        },
        onMouseDown: onMouseDown,
        onMouseEnter: (e)=>{
            e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
        },
        onMouseLeave: (e)=>{
            e.currentTarget.style.backgroundColor = 'transparent';
        }
    }), render ? render() : children);
};
// 🎯 조건부 모바일 네비게이션 컴포넌트
export const ConditionalMobileNavigation = ({ show, children, render, className = '', style = {} })=>{
    console.log('🟢 ConditionalMobileNavigation:', {
        show,
        hasRender: !!render,
        hasChildren: !!children
    });
    if (!show) return null;
    const content = render ? render() : children;
    console.log('🟢 ConditionalMobileNavigation rendering content:', content);
    return /*#__PURE__*/ React.createElement("nav", {
        className: `main-layout__mobile-nav ${className}`,
        style: style
    }, content);
};
export const ConditionalMainContent = ({ children, className = '', style = {}, renderHeader, headerLeft, headerActions, showHeader })=>{
    const theme = useTheme();
    // 헤더 표시 여부 결정
    const shouldShowHeader = showHeader !== false && (renderHeader || headerLeft || headerActions);
    // 헤더 컨텐츠 렌더링
    const renderHeaderContent = ()=>{
        // renderHeader가 있으면 우선 사용 (backward compatibility)
        if (renderHeader) {
            return renderHeader();
        }
        // 새로운 props 패턴 사용
        return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
            className: "main-layout__header-left"
        }, headerLeft), /*#__PURE__*/ React.createElement("div", {
            className: "main-layout__header-actions"
        }, headerActions));
    };
    return /*#__PURE__*/ React.createElement("main", {
        className: `main-layout__main ${className}`,
        style: style
    }, shouldShowHeader && /*#__PURE__*/ React.createElement(StyledHeader, {
        theme: theme
    }, renderHeaderContent()), /*#__PURE__*/ React.createElement(StyledMainContent, {
        theme: theme
    }, children));
};
